import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { Bell, CheckCheck, ArrowRight } from 'lucide-react';
import axios from 'axios';
import SeverityBadge from '@/components/primitives/SeverityBadge';
import ErrorState from '@/components/primitives/ErrorState';
import EmptyState from '@/components/primitives/EmptyState';
import LoadingState from '@/components/primitives/LoadingState';
import { timeAgo } from '@/hooks/useSystemStatus';
import '@/components/primitives/primitives.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : null;
};

/**
 * Notification center. Each row opens the brief in the Inbox and marks
 * the notification read. Radix Popover gives Escape to close, focus
 * management and collision handling (stays on screen on phones).
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [status, setStatus] = useState({ loaded: false, error: null });
  const [isOpen, setIsOpen] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const unmountedRef = useRef(false);

  const loadNotifications = useCallback(async () => {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${API}/notifications`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
      setStatus({ loaded: true, error: null });
    } catch (error) {
      // Rows already shown stay; the error is only surfaced when there is nothing to show.
      setStatus({ loaded: true, error });
    }
  }, []);

  const setupWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || unmountedRef.current) return;
    try {
      // An empty backend URL means the API is served from this site (the Cloudflare deployment).
      const wsUrl = (BACKEND_URL || window.location.origin).replace('https://', 'wss://').replace('http://', 'ws://');
      // The token goes in the first message, never in the URL, so it stays out of proxy and edge logs.
      const ws = new WebSocket(`${wsUrl}/ws/notifications`);
      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          setNotifications((prev) => [notification, ...prev].slice(0, 50));
          if (!notification.read) setUnreadCount((prev) => prev + 1);
        } catch { /* ignore malformed frames */ }
      };
      // Only the current socket reconnects. Under StrictMode's double mount
      // the first socket closes after the second mount has started; letting
      // it retry would open a duplicate socket and double every notification.
      ws.onclose = () => {
        if (wsRef.current === ws && !unmountedRef.current) {
          retryRef.current = setTimeout(setupWebSocket, 5000);
        }
      };
      wsRef.current = ws;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('WebSocket setup failed:', error);
      }
    }
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    loadNotifications();
    setupWebSocket();
    const interval = setInterval(loadNotifications, 30000);
    return () => {
      unmountedRef.current = true;
      clearInterval(interval);
      clearTimeout(retryRef.current);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        ws.onmessage = null;
        // Closing mid-handshake logs a "connection failed" error; close once open instead.
        if (ws.readyState === WebSocket.CONNECTING) ws.onopen = () => ws.close();
        else ws.close();
      }
    };
  }, [loadNotifications, setupWebSocket]);

  const markAllRead = async () => {
    const headers = authHeaders();
    if (!headers) return;
    const prev = { notifications, unreadCount };
    setUnreadCount(0);
    setNotifications((list) => list.map((n) => ({ ...n, read: true })));
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers });
    } catch {
      setNotifications(prev.notifications);
      setUnreadCount(prev.unreadCount);
    }
  };

  const openNotification = (n) => {
    setIsOpen(false);
    if (!n.read) {
      setNotifications((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
      const headers = authHeaders();
      if (headers && n.id) {
        axios.put(`${API}/notifications/${encodeURIComponent(n.id)}/read`, {}, { headers }).catch(() => {});
      }
    }
    // An in-app path set by the backend wins; only same-site paths are followed, never a full URL.
    const internal = typeof n.link === 'string' && n.link.startsWith('/') && !n.link.startsWith('//');
    if (internal) navigate(n.link);
    else if (n.type === 'exposure' && n.finding_id) navigate(`/exposure/dark-web?id=${encodeURIComponent(n.finding_id)}`);
    else if (n.type === 'brand_lookalike') navigate('/exposure/brand');
    else if (n.type === 'email_protection_weakened') navigate('/exposure/brand?tab=email');
    else if (n.attack_id) navigate(`/threats?id=${encodeURIComponent(n.attack_id)}`);
    else navigate('/threats');
  };

  const badge = unreadCount > 99 ? '99+' : String(unreadCount);
  const name = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <Popover.Root open={isOpen} onOpenChange={(o) => { setIsOpen(o); if (o) loadNotifications(); }}>
      <Popover.Trigger asChild>
        <button type="button" className="is-icon-btn" aria-label={name} title={name} data-testid="notification-bell">
          <Bell aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 1,
                right: 0,
                minWidth: 17,
                height: 17,
                padding: '0 4px',
                borderRadius: 9,
                background: 'var(--state-critical-strong)',
                color: 'var(--text-on-strong)',
                fontSize: 'var(--fs-2xs)',
                fontWeight: 700,
                lineHeight: '17px',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {badge}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={12}
          aria-label="Notifications"
          data-testid="notification-panel"
          className="is-card"
          style={{
            zIndex: 250,
            width: 'min(380px, calc(100vw - 24px))',
            maxHeight: 'min(480px, var(--radix-popover-content-available-height))',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'var(--shadow-lg)',
            borderColor: 'var(--border-strong)',
            overflow: 'hidden',
          }}
        >
          <div className="is-card-head" style={{ padding: '10px 14px' }}>
            <h2 className="t-heading" style={{ fontSize: 'var(--fs-base)' }}>
              Notifications
              {unreadCount > 0 && <span className="t-meta" style={{ marginLeft: 8 }}>{unreadCount} unread</span>}
            </h2>
            {unreadCount > 0 && (
              <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={markAllRead}>
                <CheckCheck aria-hidden="true" /> Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {!status.loaded ? (
              <LoadingState label="notifications" rows={3} />
            ) : status.error && notifications.length === 0 ? (
              <ErrorState variant="compact" title="Couldn't load notifications" error={status.error} onRetry={loadNotifications} />
            ) : notifications.length === 0 ? (
              <EmptyState
                variant="compact"
                icon={Bell}
                title="No notifications yet"
                description="New critical and high briefs that match your profile will appear here."
              />
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {notifications.map((n) => (
                  <li key={n.id || `${n.attack_id}-${n.created_at}`} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <button
                      type="button"
                      onClick={() => openNotification(n)}
                      className="is-notif-row"
                      data-unread={n.read ? undefined : 'true'}
                      data-testid={`notification-${n.id}`}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          {n.severity && <SeverityBadge severity={n.severity} size="sm" />}
                          {!n.read && <span className="sr-only">Unread.</span>}
                          <span className="t-meta">{timeAgo(n.created_at)}</span>
                        </span>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 'var(--fs-sm)',
                            fontWeight: n.read ? 500 : 600,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {n.title}
                        </span>
                        {n.message && (
                          <span
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              marginTop: 2,
                              fontSize: 'var(--fs-xs)',
                              lineHeight: 1.45,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {n.message}
                          </span>
                        )}
                      </span>
                      <ArrowRight size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 4, color: 'var(--text-muted)' }} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="is-card-foot" style={{ padding: '8px 14px' }}>
            <button
              type="button"
              className="is-btn is-btn--ghost is-btn--sm"
              onClick={() => { setIsOpen(false); navigate('/threats'); }}
            >
              View all in Inbox <ArrowRight aria-hidden="true" />
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default NotificationBell;
